import React, { useState } from 'react';
import { Box, Typography, Button, Avatar } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ICompanyDetails } from '../../redux/company/types';
import { ReviewFormModal } from './ReviewFormModal';
import { useSelector } from 'react-redux';
import { userProfileSelector } from '../../selectors/selectors';
import { Edit } from 'lucide-react';
import { useAuthModal } from '../../module/AuthenticationModule';

interface ICompanyHeader {
    companyDetails?: ICompanyDetails;
    onEditClick?: () => void;
}

const CompanyHeader: React.FC<ICompanyHeader> = ({ companyDetails, onEditClick }) => {
    const { themeStyle } = useThemeContext();
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const currentUser = useSelector(userProfileSelector);
    const { withAuth } = useAuthModal();

    // Check if current user is the creator of the company
    const isCompanyCreator = currentUser?._id === companyDetails?.company?.created_by?._id;

    return (
        <Box
            sx={{
                backgroundColor: theme[themeStyle].pure_black,
                borderBottom: `0.5px solid ${theme[themeStyle].filters_white}`,
                position: { xs: 'static', sm: 'sticky' },
                top: 0,
                zIndex: 50,
                py: 2,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}>
                    <Avatar
                        src={companyDetails?.company.profile_picture}
                        sx={{
                            width: 64,
                            height: 64,
                            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                        }}>
                        {companyDetails?.company.company_name[0]}
                    </Avatar>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                fontWeight: 'bold',
                            }}>
                            {companyDetails?.company.company_name}
                        </Typography>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                textTransform: 'capitalize',
                            }}>
                            {companyDetails?.company.company_type}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexDirection: { xs: 'row', sm: 'row' },
                    }}>
                    {isCompanyCreator && onEditClick && (
                        <Button
                            variant="outlined"
                            startIcon={<Edit size={16} />}
                            onClick={onEditClick}
                            sx={{
                                borderColor: theme[themeStyle].tag_white,
                                color: theme[themeStyle].tag_white,
                                '&:hover': {
                                    bgcolor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].true_white,
                                    borderColor: theme[themeStyle].highlight,
                                },
                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                            }}>
                            Edit Company
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        onClick={withAuth(() => setIsReviewModalOpen(true))}
                        sx={{
                            borderColor: theme[themeStyle].tag_white,
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight,
                                color: theme[themeStyle].true_white,
                                borderColor: theme[themeStyle].highlight,
                            },
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        Leave a review
                    </Button>
                    <Button
                        sx={{
                            backgroundColor: theme[themeStyle].primary_orange,
                            color: theme[themeStyle].pure_black,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.8,
                            },
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        View Jobs
                    </Button>
                </Box>
            </Box>

            {/* Review Form Modal */}
            <ReviewFormModal
                open={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                companyId={companyDetails?.company._id || ''}
                companyName={companyDetails?.company.company_name || ''}
            />
        </Box>
    );
};

export { CompanyHeader };
